import { useCallback } from 'react';
import { STORAGE } from '../constants';
import { LocalStorageService } from '../services/local-storage.service';

interface CampaignData {
  name: string;
  companies: string;
  account: string;
  keyBase: string;
  typeCampaign: 'DEFAULT' | 'FALLBACK' | 'FLOW';
  headers: string;
  programing?: boolean;
  dateHour?: string;
  timeZone?: string;
  dosing?: boolean;
  initHour?: string;
  finishHour?: string;
  uniqueSend?: boolean;
  blacklistSwitch?: boolean;
  blacklist: string[];
  channelFlows?: string[];
  defaultChannel?: string;
  objects?: string;
  events?: any;
  channel: string;
  type: string;
  msgSMS: string;
  total_character: number;
  total_credits: number;
  masks: string;
  complementsSwitch: boolean;
  complements: string;
  phone_number: string;
  msgWhatsAppLink: string;
  urlLink: string;
  accents: boolean;
  previewRCS?: any;
  optionsExamples?: any;
  typeRCS: string;
  templatesRCS: string;
  agentsRCS: string;
  templatesWa: string;
  accountsWa: string;
  vars?: any;
  templatesEmail?: string;
  accountsEmail?: string;
  channelfallback: string;
  trySecondFallback: boolean;
  secondChannelfallback: string;
  subjectEmail?: string;
  varsEmail?: any;
}

const generalData = (data: CampaignData): any => {
  const session = LocalStorageService.get(STORAGE.SESSION);
  const result: any = {
    account: data.account || '',
    user: session._id || '',
    name: data.name || '',
    db: data.keyBase || '',
    type: data.typeCampaign || 'JOURNEY', // ✅ Para FLOW usar JOURNEY
    phone_number_column: data.headers || '',
    send_single_messages: data.uniqueSend || false
  };

  if (data.programing) {
    result.message_schedule = {
      start_date: data.dateHour ?? '',
      timezone: data.timeZone ?? ''
    };
  }

  if (data.dosing) {
    result.message_date_window = {
      start_date: data.initHour ?? '',
      end_date: data.finishHour ?? ''
    };
  }

  if (data.blacklistSwitch && data.blacklist?.length) {
    result.blacklists = data.blacklist;
  }
  if (data.typeCampaign === 'FLOW') {
    result.goal = data.objects === 'delivered_reads' ? 'DELIVERED' : 'CONVERSATIONS';
    result.default_channel = mapChannelName(data.defaultChannel || '');
  }

  return result;
};
const mapChannelName = (channel: string): string => {
  const channelMap: Record<string, string> = {
    wa: 'WHATSAPP',
    sms: 'SMS',
    rcs: 'RCS_CONVERSATIONAL', // Por defecto
    email: 'EMAIL'
  };
  return channelMap[channel] || channel.toUpperCase();
};
const mapRCSType = (typeRCS: string): string => {
  const rcsMap: Record<string, string> = {
    rcs_basic: 'RCS_BASIC',
    rcs_single: 'RCS_SINGLE',
    rcs_conversational: 'RCS_CONVERSATIONAL'
  };
  return rcsMap[typeRCS] || 'RCS_CONVERSATIONAL';
};
const processChannelEvents = (channel: string, events: any, channelData: any) => {
  const channelEvents = events?.[channel];
  if (!channelEvents) return channelData;
  if (channelEvents.failed && channelEvents.failedChannel) {
    channelData.on_error = {
      status: true,
      channel: mapChannelName(channelEvents.failedChannel)
    };
  }
  if (['wa', 'rcs', 'email'].includes(channel) && channelEvents.followUp) {
    if (channelEvents.followUpTime && channelEvents.followUpTimeUnit) {
      const timeMultiplier = channelEvents.followUpTimeUnit === 'hour' ? 60 : 1;
      const timeInMinutes = parseInt(channelEvents.followUpTime) * timeMultiplier;

      channelData.on_follow_up = {
        status: true,
        time: timeInMinutes
      };
      if (channelEvents.followUpOnSent && channelEvents.followUpTemplateSent) {
        channelData.on_follow_up.on_sent = {
          status: true,
          template: channelEvents.followUpTemplateSent
        };
      }

      if (channelEvents.followUpOnDelivered && channelEvents.followUpTemplateDelivered) {
        channelData.on_follow_up.on_delivered = {
          status: true,
          template: channelEvents.followUpTemplateDelivered
        };
      }
    }
  }
  if (['wa', 'rcs'].includes(channel) && channelEvents.changeChannel) {
    if (
      channelEvents.changeChannelTime &&
      channelEvents.changeChannelTimeUnit &&
      channelEvents.changeChannelTarget
    ) {
      const timeMultiplier = channelEvents.changeChannelTimeUnit === 'hour' ? 60 : 1;
      const timeInMinutes = parseInt(channelEvents.changeChannelTime) * timeMultiplier;

      channelData.on_change_channel = {
        status: true,
        time: timeInMinutes
      };
      if (channelEvents.changeChannelOnSent) {
        channelData.on_change_channel.on_sent = {
          status: true,
          channel: mapChannelName(channelEvents.changeChannelTarget)
        };
      }

      if (channelEvents.changeChannelOnDelivered) {
        channelData.on_change_channel.on_delivered = {
          status: true,
          channel: mapChannelName(channelEvents.changeChannelTarget)
        };
      }
    }
  }

  return channelData;
};

const useCampaignsStructures = () => {
  const structureData = useCallback((data: CampaignData) => {
    if (data.typeCampaign === 'DEFAULT') {
      const result = generalData(data);
      const channels: any[] = [];

      switch (data.channel) {
        case 'sms':
          channels.push({
            type: 'SMS',
            sms: {
              message: data.msgSMS || '',
              mask: data.masks || '',
              ...(data.complementsSwitch && {
                complement: {
                  type: data.complements || 'WHATSAPP_LINK',
                  ...(data.complements === 'WHATSAPP_LINK'
                    ? {
                        whatsapp_link: {
                          phone_number: data.phone_number ? `+52${data.phone_number}` : '',
                          text: data.msgWhatsAppLink || ''
                        }
                      }
                    : {}),
                  ...(data.complements === 'LINK' ? { link: data.urlLink || '' } : {})
                }
              }),
              has_accents: data.accents || false
            }
          });
          break;

        case 'wa':
          channels.push({
            type: 'WHATSAPP',
            whatsapp: {
              template: data.templatesWa || '',
              waba: data.accountsWa || '',
              vars: data.vars || {}
            }
          });
          break;

        case 'rcs':
          const extractVariables = (obj: any, variables: Set<string>) => {
            const regex = /{{\s*([\w\-]+)\s*}}/g;

            const traverse = (value: any) => {
              if (typeof value === 'string') {
                let match;
                while ((match = regex.exec(value)) !== null) {
                  variables.add(match[1]);
                }
              } else if (Array.isArray(value)) {
                value.forEach((item) => traverse(item));
              } else if (typeof value === 'object' && value !== null) {
                Object.values(value).forEach((val) => traverse(val));
              }
            };

            traverse(obj);
          };

          const variablesFound = new Set<string>();
          extractVariables(data.previewRCS?.preview || [], variablesFound);

          const optionsExamples = data.optionsExamples || [];
          const vars: Record<string, string> = {};
          const missingVars: string[] = [];

          variablesFound.forEach((variable) => {
            const foundOption = optionsExamples.find((example: any) => example.header === variable);
            if (foundOption) {
              vars[`{{${variable}}}`] = foundOption.header;
            } else {
              missingVars.push(variable);
            }
          });

          if (missingVars.length > 0) {
            return {
              error: 'variables_not_found',
              variables: missingVars
            };
          }

          const typeKey = data.typeRCS.toLowerCase();
          channels.push({
            type: data.typeRCS.toUpperCase(),
            [typeKey]: {
              template: data.templatesRCS || '',
              agent: data.agentsRCS || '',
              vars: Object.keys(vars).length ? vars : {}
            }
          });
          break;

        case 'email':
          const flattenedVarsEmail = {};
          if (data.varsEmail) {
            Object.values(data.varsEmail).forEach((sectionVars: any) => {
              Object.assign(flattenedVarsEmail, sectionVars);
            });
          }
          channels.push({
            type: 'EMAIL',
            email: {
              template: data.templatesEmail || '',
              mailing_account: data.accountsEmail || '',
              subject: data.subjectEmail || '',
              vars: flattenedVarsEmail
            }
          });
          break;

        default:
          return {
            error: 'invalid_channel',
            message: `Canal no válido: ${data.channel}`
          };
      }

      result.channels = channels;
      return result;
    }
    if (data.typeCampaign === 'FLOW') {
      const result = generalData(data);
      const flows: any = {};
      data.channelFlows?.forEach((channel) => {
        let flowData: any = {};

        switch (channel) {
          case 'wa':
            flowData = {
              waba_account: data.accountsWa || '',
              template: data.templatesWa || ''
            };
            flows.whatsapp = processChannelEvents('wa', data.events, flowData);
            break;

          case 'sms':
            flowData = {
              mask: data.masks || '',
              template: data.msgSMS || '' // Mensaje directo para SMS
            };
            flows.sms = processChannelEvents('sms', data.events, flowData);
            break;

          case 'rcs':
            const rcsType = mapRCSType(data.typeRCS);
            const rcsKey = `rcs_${data.typeRCS?.split('_')[1] || 'conversational'}`;

            flowData = {
              agent: data.agentsRCS || '',
              template: data.templatesRCS || ''
            };
            flows[rcsKey] = processChannelEvents('rcs', data.events, flowData);
            break;

          case 'email':
            const flattenedVarsEmail = {};
            if (data.varsEmail) {
              Object.values(data.varsEmail).forEach((sectionVars: any) => {
                Object.assign(flattenedVarsEmail, sectionVars);
              });
            }
            flowData = {
              email_account: data.accountsEmail || '',
              template: data.templatesEmail || '',
              subject: data.subjectEmail || '',
              vars: flattenedVarsEmail
            };
            flows.email = processChannelEvents('mail', data.events, flowData);
            break;
        }
      });

      result.flows = flows;
      return result;
    }
    if (data.typeCampaign === 'FALLBACK') {
      const result = generalData(data);
      const extractVariables = (obj: any, variables: Set<string>) => {
        const regex = /{{\s*([\w\-]+)\s*}}/g;

        const traverse = (value: any) => {
          if (typeof value === 'string') {
            let match;
            while ((match = regex.exec(value)) !== null) {
              variables.add(match[1]);
            }
          } else if (Array.isArray(value)) {
            value.forEach((item) => traverse(item));
          } else if (typeof value === 'object' && value !== null) {
            Object.values(value).forEach((val) => traverse(val));
          }
        };

        traverse(obj);
      };

      const channels: any[] = [];
      if (
        data.channel === 'sms' ||
        data.channelfallback === 'sms' ||
        data.secondChannelfallback === 'sms'
      ) {
        channels.push({
          type: 'SMS',
          sms: {
            message: data.msgSMS || '',
            total_character: data.total_character || 0,
            total_credits: data.total_credits || 0,
            mask: data.masks || '',
            ...(data.complementsSwitch && {
              complement: {
                type: data.complements || 'WHATSAPP_LINK',
                ...(data.complements === 'WHATSAPP_LINK'
                  ? {
                      whatsapp_link: {
                        phone_number: data.phone_number ? `+52${data.phone_number}` : '',
                        text: data.msgWhatsAppLink || ''
                      }
                    }
                  : {}),
                ...(data.complements === 'LINK' ? { link: data.urlLink || '' } : {})
              }
            }),
            has_accents: data.accents || false
          },
          index: data.channel === 'sms' ? 0 : data.channelfallback === 'sms' ? 1 : 2
        });
      }
      if (
        data.channel === 'wa' ||
        data.channelfallback === 'wa' ||
        data.secondChannelfallback === 'wa'
      ) {
        channels.push({
          type: 'WHATSAPP',
          whatsapp: {
            template: data.templatesWa || '',
            waba: data.accountsWa || '',
            vars: data.vars || {}
          },
          index: data.channel === 'wa' ? 0 : data.channelfallback === 'wa' ? 1 : 2
        });
      }
      if (
        data.channel === 'rcs' ||
        data.channelfallback === 'rcs' ||
        data.secondChannelfallback === 'rcs'
      ) {
        const variablesFound = new Set<string>();
        extractVariables(data.previewRCS?.preview || [], variablesFound);

        const optionsExamples = data.optionsExamples || [];
        const vars: Record<string, string> = {};
        const missingVars: string[] = [];

        variablesFound.forEach((variable) => {
          const foundOption = optionsExamples.find((example: any) => example.header === variable);
          if (foundOption) {
            vars[`{{${variable}}}`] = foundOption.header;
          } else {
            missingVars.push(variable);
          }
        });

        const typeKey = data.typeRCS.toLowerCase();
        const index = data.channel === 'rcs' ? 0 : data.channelfallback === 'rcs' ? 1 : 2;

        channels.push({
          type: data.typeRCS.toUpperCase(),
          [typeKey]: {
            template: data.templatesRCS || '',
            agent: data.agentsRCS || '',
            vars: Object.keys(vars).length ? vars : {}
          },
          index
        });

        if (missingVars.length > 0) {
          return {
            error: 'variables_not_found',
            variables: missingVars
          };
        }
      }
      if (
        data.channel === 'email' ||
        data.channelfallback === 'email' ||
        data.secondChannelfallback === 'email'
      ) {
        const flattenedVarsEmail = {};
        if (data.varsEmail) {
          Object.values(data.varsEmail).forEach((sectionVars: any) => {
            Object.assign(flattenedVarsEmail, sectionVars);
          });
        }

        channels.push({
          type: 'EMAIL',
          email: {
            template: data.templatesEmail || '',
            mailing_account: data.accountsEmail || '',
            subject: data.subjectEmail || '',
            vars: flattenedVarsEmail
          },
          index: data.channel === 'email' ? 0 : data.channelfallback === 'email' ? 1 : 2
        });
      }

      result.channels = channels;
      return result;
    }

    return null;
  }, []);

  return { structureData };
};

export default useCampaignsStructures;
